import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboardIcon, PlusCircle, Bell, Power, IndianRupee, ChevronDown, FileText, Settings, LogOut, BookOpen } from 'lucide-react';
import api from '../services/api';

const TopNavbar = ({ userRole, onLogout }) => {
  const navigate = useNavigate();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [documentsDropdownOpen, setDocumentsDropdownOpen] = useState(false);
const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mastersDropdownOpen, setMastersDropdownOpen] = useState(false);
  const [reportsDropdownOpen, setReportsDropdownOpen] = useState(false);
  const [formsDropdownOpen, setFormsDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Refs for dropdowns and notifications for click outside handling
  const dropdownRef = useRef(null);
  const documentsDropdownRef = useRef(null);
  const notificationsRef = useRef(null);
  const userMenuRef = useRef(null);
const mastersDropdownRef = useRef(null);
  const formsDropdownRef = useRef(null);
  
  const reportsDropdownRef = useRef(null);
  // Get user role from localStorage if not provided
  const effectiveUserRole = userRole || localStorage.getItem('userRole') || '';

  // Function to handle clicking outside dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (documentsDropdownRef.current && !documentsDropdownRef.current.contains(event.target)) {
        setDocumentsDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (mastersDropdownRef.current && !mastersDropdownRef.current.contains(event.target)) {
        setMastersDropdownOpen(false);
      }
if (reportsDropdownRef.current && !reportsDropdownRef.current.contains(event.target)) {
        setReportsDropdownOpen(false);
      }
      if (formsDropdownRef.current && !formsDropdownRef.current.contains(event.target)) {
        setFormsDropdownOpen(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

// Fetch forms list
  const fetchForms = async () => {
    try {
      const response = await fetch(`${api.BASE_URL}/form/list`, {
        headers: {
          'Authorization': localStorage.getItem('authToken')
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (!result.error && result.data && Array.isArray(result.data)) {
          setForms(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    }
  };

// Fetch notifications when component mounts
  useEffect(() => {
    fetchNotifications();
     fetchForms(); // Fe
    // Set up polling for notifications every 3 minutes
    const interval = setInterval(fetchNotifications, 180000);
    
    // 🔥 NEW: Listen for manual refresh requests from modal
    const handleRefreshRequest = () => {
      console.log('🔥 Notification refresh requested from modal');
      fetchNotifications();
    };
    
    window.addEventListener('refreshNotifications', handleRefreshRequest);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshNotifications', handleRefreshRequest);
    };
  }, []);
const handleSessionExpiration = () => {
  console.log('🔥 handleSessionExpiration called!');
  
  // Clear stored authentication data
  localStorage.removeItem('authToken');
  localStorage.removeItem('userRole');
  
  // Call onLogout if provided
  if (onLogout) {
    console.log('🔥 Calling onLogout...');
    onLogout();
  }
  
  // Try React Router navigation first
  console.log('🔥 Trying React Router navigation...');
  try {
    navigate('/login');
    console.log('🔥 React Router navigation called');
  } catch (error) {
    console.log('🔥 React Router failed:', error);
  }
  
  // Force navigation using window.location as backup
  console.log('🔥 Using window.location as backup...');
  setTimeout(() => {
    window.location.href = '/login';
    console.log('🔥 window.location.href set to /login');
  }, 100);
  
  console.log('🔥 handleSessionExpiration finished');
};

const fetchNotifications = async () => {
  console.log('🔥 fetchNotifications called!');
  setLoading(true);
  
  try {
    console.log('🔥 About to call API...');
    console.log('🔥 API object:', api);
    console.log('🔥 API notification object:', api.notification);
    console.log('🔥 getNotifications function:', api.notification.getNotifications);
    
    // Add a timeout to the API call
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('API call timeout after 10 seconds')), 10000)
    );
    
    const apiPromise = api.notification.getNotifications();
    console.log('🔥 API promise created:', apiPromise);
    
    const response = await Promise.race([apiPromise, timeoutPromise]);
    console.log('🔥 API response received:', response);
    
    // Check for session expiration error
    if (response.error === "Session expired, please login again") {
      console.log('🔥 Session expired detected!');
      handleSessionExpiration();
      return;
    }
    
    if (!response.error) {
      console.log('🔥 Setting notifications:', response.data);
      setNotifications(response.data || []);
    } else {
      console.log('🔥 API returned error:', response.error);
    }
    
  } catch (error) {
    console.log('🔥 Error caught:', error);
    console.log('🔥 Error type:', typeof error);
    console.log('🔥 Error message:', error.message);
    console.log('🔥 Error stack:', error.stack);
    
    // Check if it's our timeout error
    if (error.message === 'API call timeout after 10 seconds') {
      console.log('🔥 API call timed out!');
    }
    
    // Check for session expiration in error
    if (error.message && error.message.includes('401')) {
      console.log('🔥 401 error detected, calling session expiration');
      handleSessionExpiration();
    }
    
    console.error('Error fetching notifications:', error);
  } finally {
    console.log('🔥 fetchNotifications finished');
    setLoading(false);
  }
};

// Simple handleNotificationClick function for TopNavbar.js
// Use this version if the notification detail API endpoint doesn't exist

const handleNotificationClick = async (notification) => {
  try {
    setNotificationsOpen(false);
    
    console.log('🔥 TopNavbar processing notification:', notification);
    
    // CORRECT type detection based on your actual notification data
    let itemType = 'training'; // default
    
    // Use the title field for reliable detection
    if (notification.title === 'New admin') {
      itemType = 'admin';
      console.log('🔥 Detected as ADMIN based on title');
    } else if (notification.title === 'New infra') {
      itemType = 'infra';
      console.log('🔥 Detected as INFRA based on title');
    } else if (notification.title === 'New training') {
      itemType = 'training';
      console.log('🔥 Detected as TRAINING based on title');
    } else {
      // Fallback to code-based detection
      const code = notification.code || '';
      if (code.includes('/ADM/')) {
        itemType = 'admin';
        console.log('🔥 Detected as ADMIN based on code');
      } else if (code.includes('/WOR/') || code.includes('/CML/') || code.includes('/INF/')) {
        itemType = 'infra';
        console.log('🔥 Detected as INFRA based on code');
      } else {
        itemType = 'training';
        console.log('🔥 Defaulting to TRAINING');
      }
    }
    
    const itemId = notification.proj_id;
    
    console.log('🔥 Final detection - ID:', itemId, 'Type:', itemType);
    
    // Navigate to dashboard
    navigate('/dashboard', { 
      state: { 
        openNotification: {
          id: itemId,
          proj_id: itemId,
          type: itemType,
          title: notification.title || notification.name
        }
      } 
    });
    
  } catch (error) {
    console.error('🔥 Error in handleNotificationClick:', error);
  }
};
// Helper function removed since we're not navigating anywhere

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    onLogout();
    navigate('/login');
  };
const goToSFCReport = () => {
  setReportsDropdownOpen(false);
  navigate('/report-sfc');
};

const goToTCFIDFReport = () => {
  setReportsDropdownOpen(false);
  navigate('/report-tcf-idf');
};

const goToGoIReport = () => {
  setReportsDropdownOpen(false);
  navigate('/report-goi');
};

const goToRGSReport = () => {
  setReportsDropdownOpen(false);
  navigate('/report-rgs');
};
  // Navigation functions
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToAddDocuments = () => {
    setMastersDropdownOpen(false);
    navigate('/add-documents');
  };
  
  const goToAddTraining = () => {
    setDropdownOpen(false);
    navigate('/add-training');
  };
  
  const goToAddInfra = () => {
    setDropdownOpen(false);
    navigate('/add-infra');
  };
  
  const goToAddAdmin = () => {
    setDropdownOpen(false);
    navigate('/add-admin');
  };
  
  const goToAddOthers = () => {
    setDropdownOpen(false);
    navigate('/add-others');
  };
  
  const goToLedger = () => {
    navigate('/ledger');
  };
  
  const goToSettings = () => {
    setUserMenuOpen(false);
    navigate('/settings');
  };
  
  // Document routes
  const goToBRCP = () => {
    setDocumentsDropdownOpen(false);
    navigate('/brcp');
  };
  
  const goToDRCP = () => {
    setDocumentsDropdownOpen(false);
    navigate('/drcp');
  };

  const goToReports = () => {
    navigate('/report');
  };

  // Excel route for department users - moved to masters dropdown
  const goToExcel = () => {
    setMastersDropdownOpen(false);
    navigate('/excel');
  };

  // Masters navigation routes
  const goToAddSchemes = () => {
    setMastersDropdownOpen(false);
    navigate('/add-schemes');
  };
  
  const goToAddTitles = () => {
    setMastersDropdownOpen(false);
    navigate('/add-titles');
  };
  
  const goToAddClientele = () => {
    setMastersDropdownOpen(false);
    navigate('/add-clientele');
  };

  // Count unread notifications
  const unreadCount = notifications.length;

  return (
    <header className="bg-gray-800 text-white shadow-md">
      <div className="container mx-auto px-2 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side with logos and title */}
          <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4 flex-shrink-0">
            {/* Logos container - responsive sizes */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              <img 
                src="/images/tnlogo.png" 
                alt="SIRD Logo" 
                className="h-8 w-auto sm:h-10"
                title="State Institute of Rural Development & Panchayat Raj" 
              />
              <img 
                src="/images/dept.png" 
                alt="PR Logo" 
                className="h-8 w-auto sm:h-10"
                title="Panchayat Raj" 
              />
              <img 
                src="/images/dept1.png" 
                alt="Government Logo" 
                className="h-8 w-auto sm:h-10"
                title="Government of India" 
              />
            </div>
            
            <div className="hidden md:block border-l border-gray-600 pl-3">
              <h1 className="text-white font-semibold text-lg">
              பயிற்சி (வளர்ச்சி)
              </h1>
            </div>
          </div>
          
          {/* Right side items - made responsive */}
          <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3">
            {/* Dashboard Button */}
            <button
              onClick={goToDashboard}
              className="p-1.5 sm:p-2.5 rounded-full text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none transition-colors"
              title="Go to Dashboard"
            >
              <LayoutDashboardIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="sr-only">Dashboard</span>
            </button>
            
{/* Ledger Button */}
            <button
              onClick={goToLedger}
              className="p-1.5 sm:p-2.5 rounded-full text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none transition-colors"
              title="Ledger"
            >
              <IndianRupee className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="sr-only">Ledger</span>
            </button>
            
            {/* Forms Dropdown */}
            <div className="relative" ref={formsDropdownRef}>
              <button 
                onClick={() => setFormsDropdownOpen(!formsDropdownOpen)}
                className="bg-teal-500 hover:bg-teal-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium focus:outline-none transition-colors flex items-center space-x-1"
                title="Forms"
              >
                <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
       
                <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200" 
                  style={{ transform: formsDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }} 
                />
              </button>
              
              {/* Forms dropdown menu */}
              {formsDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-50 overflow-hidden animate-dropdown origin-top-right">
                  <div className="py-1 max-h-80 overflow-y-auto">
                    {forms.length > 0 ? (
                      forms.map((form) => (
        <button
                          key={form.id}
                          onClick={() => {
                            setFormsDropdownOpen(false);
                            navigate(`/form/${form.id}`);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
                        >
                          <span className="mr-2">📝</span> {form.name || form.title}
                        </button>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        No forms available
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            

            
      {/* Reports dropdown for department users */}
{effectiveUserRole === 'department' && (
  <div className="relative" ref={reportsDropdownRef}>
    <button 
      onClick={() => setReportsDropdownOpen(!reportsDropdownOpen)}
      className="bg-indigo-500 hover:bg-indigo-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium focus:outline-none transition-colors flex items-center space-x-1"
      title="Reports"
    >
      <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
      <span className="hidden sm:inline ml-1">Reports</span>
      <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200" 
        style={{ transform: reportsDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }} 
      />
    </button>
    
    {/* Reports dropdown menu */}
    {reportsDropdownOpen && (
      <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg z-50 overflow-hidden animate-dropdown origin-top-right">
        <div className="py-1">
          <button
            onClick={goToReports}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
          >
            <span className="mr-2">📊</span> View All Reports
          </button>
          <button
            onClick={goToSFCReport}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
          >
            <span className="mr-2">📋</span> SFC Works
          </button>
          <button
            onClick={goToTCFIDFReport}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
          >
            <span className="mr-2">📄</span> TCF/IDF Works
          </button>
          <button
            onClick={goToGoIReport}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
          >
            <span className="mr-2">🏛️</span> GoI Works
          </button>
          <button
            onClick={goToRGSReport}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
          >
            <span className="mr-2">🏗️</span> RRGSA Works
          </button>
        </div>
      </div>
    )}
  </div>
)}
{/* Masters dropdown for department and state users */}
{(effectiveUserRole === 'department' || effectiveUserRole === 'state') && (
  <div className="relative" ref={mastersDropdownRef}>
    <button 
      onClick={() => setMastersDropdownOpen(!mastersDropdownOpen)}
      className="bg-purple-500 hover:bg-purple-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium focus:outline-none transition-colors flex items-center space-x-1"
      title="Masters Management"
    >
      <BookOpen className="h-4 w-4 sm:h-5 sm:w-5" />
      <span className="hidden sm:inline ml-1">Masters</span>
      <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200" 
        style={{ transform: mastersDropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }} 
      />
    </button>
    
    {/* Masters dropdown menu with animation */}
    {mastersDropdownOpen && (
      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 overflow-hidden animate-dropdown origin-top-right">
        <div className="py-1">
          {/* Show all options for department users */}
          {effectiveUserRole === 'department' && (
            <>
              <button
                onClick={goToAddSchemes}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
              >
                <span className="mr-2">📊</span> Schemes
              </button>
              <button
                onClick={goToAddTitles}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
              >
                <span className="mr-2">📝</span> Title
              </button>
              <button
                onClick={goToAddClientele}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
              >
                <span className="mr-2">👥</span> Clientele
              </button>
            </>
          )}
          
          {/* Documents option for both department and state users */}
          <button
            onClick={goToAddDocuments}
            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
          >
            <span className="mr-2">📄</span> Documents
          </button>
          
          {/* Portal Uploads only for department users */}
          {effectiveUserRole === 'department' && (
            <button
              onClick={goToExcel}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
            >
              <span className="mr-2">📊</span> Portal Uploads
            </button>
          )}
        </div>
      </div>
    )}
  </div>
)}
            {/* Add dropdown button for non-department users */}
            {effectiveUserRole !== 'department' && (
              <div className="relative" ref={dropdownRef}>
                <button 
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="bg-green-500 hover:bg-green-600 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium focus:outline-none transition-colors flex items-center space-x-1"
                  title="Add Options"
                >
                  <PlusCircle className="h-4 w-4 sm:h-5 sm:w-5" />
                  <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-200" 
                    style={{ transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }} 
                  />
                </button>
                
                {/* Dropdown menu with animation */}
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 overflow-hidden animate-dropdown origin-top-right">
                    <div className="py-1">
                      <button
                        onClick={goToAddTraining}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
                      >
                        <span className="mr-2">⚙️</span> Training
                      </button>
                      <button
                        onClick={goToAddInfra}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
                      >
                        <span className="mr-2">🏢</span> Infrastructure
                      </button>
                      {/* Admin option now available for all users */}
                      <button
                        onClick={goToAddAdmin}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
                      >
                        <span className="mr-2">👤</span> Admin
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
           
         
            
            {/* Notifications */}
            <div className="relative" ref={notificationsRef}>
              <button
                className="p-1.5 sm:p-2.5 rounded-full text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none"
                onClick={() => setNotificationsOpen(!notificationsOpen)}
                title="View Notifications"
              >
                <Bell className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="sr-only">Notifications</span>
                
                {/* Badge for unread notifications */}
                {unreadCount > 0 && (
                  <span className="absolute top-0 right-0 bg-red-500 text-white text-xs w-3 h-3 sm:w-4 sm:h-4 flex items-center justify-center rounded-full text-[0.6rem] sm:text-xs">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification dropdown */}
              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-64 sm:w-72 md:w-80 bg-white text-gray-800 rounded-md shadow-lg z-50 animate-dropdown origin-top-right">
                  <div className="px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-100 flex justify-between items-center">
                    <h3 className="text-xs sm:text-sm font-medium text-gray-700">Notifications</h3>
                    {unreadCount > 0 && (
                      <span className="text-[0.6rem] sm:text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full">
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                  
                  <div className="max-h-60 sm:max-h-72 overflow-y-auto">
                    {loading ? (
                      <div className="py-4 text-center text-gray-500 text-xs sm:text-sm">
                        <div className="animate-spin mx-auto h-4 w-4 sm:h-5 sm:w-5 border-2 border-blue-500 border-t-transparent rounded-full mb-2"></div>
                        Loading...
                      </div>
                    ) : notifications.length > 0 ? (
                      notifications.map(notification => (
                        <div
                          key={notification.id}
                          className={`px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${notification.read === false ? 'bg-blue-50' : ''}`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex justify-between">
                            <p className="font-medium text-xs sm:text-sm text-gray-700">{notification.title || 'New notification'}</p>
                            {notification.read === false && (
                              <span className="inline-block w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600 mt-1">
                            {notification.name}
                          </p>
                          <p className="text-[0.6rem] sm:text-xs text-gray-500 mt-1 font-mono">
                            {notification.code}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="py-4 sm:py-6 text-center text-gray-500 text-xs sm:text-sm">
                        No notifications
                      </div>
                    )}
                  </div>
                  
                  {notifications.length > 0 && (
                    <div className="px-3 py-1.5 sm:px-4 sm:py-2 border-t border-gray-100 text-center">
                      {/* <button 
                        className="text-[0.6rem] sm:text-xs text-blue-600 hover:text-blue-800"
                        onClick={() => {
                          setNotificationsOpen(false);
                          navigate('/all-notifications');
                        }}
                      >
                        View all notifications
                      </button> */}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* User Menu (Power button replaced with dropdown) */}
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="p-1.5 sm:p-2.5 rounded-full text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none transition-colors"
                title="User Options"
              >
                <Power className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="sr-only">User Menu</span>
              </button>
              
              {/* User menu dropdown */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 overflow-hidden animate-dropdown origin-top-right">
                  <div className="py-1">
                    <button
                      onClick={goToSettings}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
                    >
                      <Settings className="h-4 w-4 mr-2 text-gray-500" />
                      Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center"
                    >
                      <LogOut className="h-4 w-4 mr-2 text-gray-500" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

// Add animation keyframes to the global stylesheet
const style = document.createElement('style');
style.textContent = `
  @keyframes dropdown {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-10px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
  
  .animate-dropdown {
    animation: dropdown 0.15s ease-out forwards;
  }
`;
document.head.appendChild(style);

export default TopNavbar;